"use strict";

/*==============================================================================*
*TRESTLE W005
*MY DAILY WELLNESS LOG
*Version 2.0
*
*DAILY DATA MODEL
*
*All wellness log entries are optional.
*
*LATEST STATE
*Medication
*Water
*Mood
*Stress
*Sleep
*
*EVENT HISTORY + LATEST ENTRY
*Blood Sugar
*Meal
*Movement
*
*Blood Sugar, Meal and Medication additionally store the actual event time.
*
*All information is stored in localStorage.
*
*IMPORTANT
*--------
*Backend/storage architecture remains unchanged.
*Existing event names and public API remain unchanged for compatibility.
*==============================================================================*/

(function(){


/*==============================================================================*
*CONFIGURATION
*==============================================================================*/

const W005_CONFIG={

    storageKey:
        "trestle_w005_quick_checkin",

    version:
        "2.0",

    noteMaxLength:
        120,

    feedbackDuration:
        5000,

    historyTypes:[
        "bloodSugar",
        "meal",
        "movement"
    ]

};


/*==============================================================================*
*CHECK-IN DEFINITIONS
*==============================================================================*/

const CHECKINS={

    bloodSugar:{

        title:"Blood Sugar",

        icon:"🩸",

        emptyText:"Add reading",

        history:true

    },


    meal:{

        title:"Meal",

        icon:"🍽️",

        emptyText:"Add meal",

        history:true

    },


    medication:{

        title:"Medication",

        icon:"💊",

        emptyText:"Add status",

        history:false

    },


    water:{

        title:"Water",

        icon:"💧",

        emptyText:"Add glasses",

        history:false

    },


    movement:{

        title:"Movement",

        icon:"🚶",

        emptyText:"Add activity",

        history:true

    },


    mood:{

        title:"Mood",

        icon:"😊",

        emptyText:"How do you feel?",

        history:false

    },


    stress:{

        title:"Stress",

        icon:"😟",

        emptyText:"Add level",

        history:false

    },


    sleep:{

        title:"Sleep",

        icon:"😴",

        emptyText:"Add sleep",

        history:false

    }

};


/*==============================================================================*
*RUNTIME STATE
*==============================================================================*/

let root=null;

let activeType=null;

let activeChoices={};

let dailyData=null;

let eventsBound=false;

let feedbackTimer=null;


/*==============================================================================*
*TODAY DATE KEY
*==============================================================================*/

function getTodayKey(){

    const now=
        new Date();


    const year=
        now.getFullYear();


    const month=
        String(
            now.getMonth()+1
        ).padStart(
            2,
            "0"
        );


    const day=
        String(
            now.getDate()
        ).padStart(
            2,
            "0"
        );


    return(
        year+
        "-" +
        month+
        "-" +
        day
    );

}


/*==============================================================================*
*AUTOMATIC LOG TIMESTAMP
*==============================================================================*/

function getTimestamp(){

    return new Date().toISOString();

}


/*==============================================================================*
*CURRENT TIME FOR TIME INPUT
*==============================================================================*/

function getCurrentTimeValue(){

    const now=
        new Date();


    const hours=
        String(
            now.getHours()
        ).padStart(
            2,
            "0"
        );


    const minutes=
        String(
            now.getMinutes()
        ).padStart(
            2,
            "0"
        );


    return(
        hours+
        ":"+
        minutes
    );

}


/*==============================================================================*
*CREATE EMPTY DAY
*==============================================================================*/

function createEmptyDay(dateKey){

    return{

        version:
            W005_CONFIG.version,

        date:
            dateKey,

        createdAt:
            getTimestamp(),

        updatedAt:
            null,


        latest:{

            bloodSugar:null,

            meal:null,

            medication:null,

            water:null,

            movement:null,

            mood:null,

            stress:null,

            sleep:null

        },


        history:{

            bloodSugar:[],

            meal:[],

            movement:[]

        }

    };

}


/*==============================================================================*
*READ COMPLETE LOCAL STORAGE
*==============================================================================*/

function readStorage(){

    try{

        const raw=

            localStorage.getItem(
                W005_CONFIG.storageKey
            );


        if(!raw){

            return{

                version:
                    W005_CONFIG.version,

                days:{}

            };

        }


        const parsed=
            JSON.parse(raw);


        if(
            !parsed ||
            typeof parsed!=="object"
        ){

            throw new Error(
                "Invalid W005 localStorage data."
            );

        }


        if(
            !parsed.days ||
            typeof parsed.days!=="object"
        ){

            parsed.days={};

        }


        return parsed;

    }


    catch(error){

        console.warn(
            "TRESTLE W005 LocalStorage Read Error",
            error
        );


        return{

            version:
                W005_CONFIG.version,

            days:{}

        };

    }

}


/*==============================================================================*
*WRITE COMPLETE LOCAL STORAGE
*==============================================================================*/

function writeStorage(storage){

    try{

        storage.version=
            W005_CONFIG.version;


        localStorage.setItem(
            W005_CONFIG.storageKey,
            JSON.stringify(
                storage
            )
        );


        return true;

    }


    catch(error){

        console.warn(
            "TRESTLE W005 LocalStorage Save Error",
            error
        );


        return false;

    }

}


/*==============================================================================*
*LOAD TODAY
*==============================================================================*/

function loadToday(){

    const storage=
        readStorage();


    const today=
        getTodayKey();


    if(
        !storage.days[today]
    ){

        storage.days[today]=
            createEmptyDay(
                today
            );


        writeStorage(
            storage
        );

    }


    dailyData=
        storage.days[today];


    normalizeDayData();


    return dailyData;

}


/*==============================================================================*
*NORMALIZE DAY DATA
*==============================================================================*/

function normalizeDayData(){

    if(!dailyData){

        dailyData=
            createEmptyDay(
                getTodayKey()
            );

    }


    if(
        !dailyData.latest ||
        typeof dailyData.latest!=="object"
    ){

        dailyData.latest={};

    }


    Object.keys(
        CHECKINS
    ).forEach(

        function(type){

            if(
                typeof dailyData.latest[type]
                ==="undefined"
            ){

                dailyData.latest[type]=null;

            }

        }

    );


    if(
        !dailyData.history ||
        typeof dailyData.history!=="object"
    ){

        dailyData.history={};

    }


    W005_CONFIG.historyTypes.forEach(

        function(type){

            if(
                !Array.isArray(
                    dailyData.history[type]
                )
            ){

                dailyData.history[type]=[];

            }

        }

    );

}


/*==============================================================================*
*SAVE TODAY
*==============================================================================*/

function saveToday(){

    if(!dailyData){

        return false;

    }


    const storage=
        readStorage();


    const today=
        getTodayKey();


    dailyData.updatedAt=
        getTimestamp();


    storage.days[today]=
        dailyData;


    return writeStorage(
        storage
    );

}


/*==============================================================================*
*CREATE UNIQUE ENTRY ID
*==============================================================================*/

function createEntryId(){

    return(
        "w005_" +

        Date.now()
            .toString(36) +

        "_" +

        Math.random()
            .toString(36)
            .slice(2,8)
    );

}


/*==============================================================================*
*SAVE CHECK-IN
*==============================================================================*/

function saveCheckin(type,payload){

    if(
        !CHECKINS[type]
    ){

        return null;

    }


    if(!dailyData){

        loadToday();

    }


    const timestamp=
        getTimestamp();


    const entry={

        id:
            createEntryId(),

        type:
            type,

        timestamp:
            timestamp,

        updatedAt:
            timestamp,

        data:
            payload

    };


    dailyData.latest[type]=
        entry;


    if(
        CHECKINS[type].history===true
    ){

        if(
            !Array.isArray(
                dailyData.history[type]
            )
        ){

            dailyData.history[type]=[];

        }


        dailyData.history[type].push(
            entry
        );

    }


    if(
        !saveToday()
    ){

        return null;

    }


    return entry;

}


/*==============================================================================*
*GET LATEST
*==============================================================================*/

function getLatest(type){

    if(
        !dailyData ||
        !dailyData.latest
    ){

        return null;

    }


    return(
        dailyData.latest[type] ||
        null
    );

}


/*==============================================================================*
*GET HISTORY
*==============================================================================*/

function getHistory(type){

    if(
        !dailyData ||
        !dailyData.history ||
        !Array.isArray(
            dailyData.history[type]
        )
    ){

        return[];

    }


    return dailyData.history[type];

}


/*==============================================================================*
*FORMAT AUTOMATIC LOG TIME
*==============================================================================*/

function formatTime(timestamp){

    if(!timestamp){

        return"";

    }


    const date=
        new Date(timestamp);


    if(
        Number.isNaN(
            date.getTime()
        )
    ){

        return"";

    }


    return date.toLocaleTimeString(

        [],

        {
            hour:"numeric",
            minute:"2-digit"
        }

    );

}


/*==============================================================================*
*FORMAT EVENT TIME
*==============================================================================*/

function formatEventTime(timeValue){

    if(!timeValue){

        return"";

    }


    const parts=
        String(
            timeValue
        ).split(":");


    if(
        parts.length<2
    ){

        return timeValue;

    }


    let hour=
        Number(
            parts[0]
        );


    const minute=
        parts[1];


    if(
        !Number.isFinite(hour)
    ){

        return timeValue;

    }


    const period=
        hour>=12
            ? "PM"
            : "AM";


    hour=
        hour%12 || 12;


    return(
        hour+
        ":"+
        minute+
        " "+
        period
    );

}


/*==============================================================================*
*SAFE NUMBER
*==============================================================================*/

function numberValue(value){

    const number=
        Number(value);


    if(
        !Number.isFinite(number)
    ){

        return null;

    }


    return number;

}


/*==============================================================================*
*OPTIONAL NOTE CLEANER
*==============================================================================*/

function cleanNote(value){

    if(!value){

        return"";

    }


    return String(value)
        .trim()
        .slice(
            0,
            W005_CONFIG.noteMaxLength
        );

}


/*==============================================================================*
*ESCAPE HTML
*==============================================================================*/

function escapeHTML(value){

    if(
        value===null ||
        typeof value==="undefined"
    ){

        return"";

    }


    return String(value)

        .replace(/&/g,"&amp;")
        .replace(/</g,"&lt;")
        .replace(/>/g,"&gt;")
        .replace(/"/g,"&quot;")
        .replace(/'/g,"&#039;");

}


/*==============================================================================*
*CREATE CHOICE BUTTONS
*==============================================================================*/

function createOptions(
    name,
    options,
    selectedValue
){

    return`

        <div
            class="tr-w005-options"
            data-choice-group="${escapeHTML(name)}"
        >

            ${options.map(

                function(option){

                    const active=
                        option.value===selectedValue
                            ? " is-active"
                            : "";


                    return`

                        <button
                            type="button"
                            class="tr-w005-option${active}"
                            data-choice-name="${escapeHTML(name)}"
                            data-choice-value="${escapeHTML(option.value)}"
                        >
                            ${escapeHTML(option.label)}
                        </button>

                    `;

                }

            ).join("")}

        </div>

    `;

}


/*==============================================================================*
*TODAY'S HISTORY HTML
*==============================================================================*/

function buildHistoryHTML(type){

    if(
        !CHECKINS[type] ||
        CHECKINS[type].history!==true
    ){

        return"";

    }


    const history=
        getHistory(type);


    if(
        !history.length
    ){

        return`

            <div class="tr-w005-history">

                <div class="tr-w005-history-title">
                    Today's Entries
                </div>

                <div class="tr-w005-history-empty">
                    No entries logged yet.
                </div>

            </div>

        `;

    }


    return`

        <div class="tr-w005-history">

            <div class="tr-w005-history-title">
                Today's Entries
            </div>

            <div class="tr-w005-history-list">

                ${history.map(

                    function(entry){

                        return buildHistoryRow(
                            type,
                            entry
                        );

                    }

                ).join("")}

            </div>

        </div>

    `;

}


/*==============================================================================*
*HISTORY ROW
*==============================================================================*/

function buildHistoryRow(type,entry){

    if(
        !entry ||
        !entry.data
    ){

        return"";

    }


    const data=
        entry.data;


    let title="";
    let meta="";
    let note="";


    switch(type){


        case "bloodSugar":

            title=
                escapeHTML(
                    data.value +
                    " " +
                    (data.unit || "mg/dL")
                );


            if(data.context){

                title+=
                    " · " +
                    escapeHTML(
                        data.context
                    );

            }


            meta=
                data.eventTime

                    ? "At " +
                      formatEventTime(
                          data.eventTime
                      )

                    : "Logged " +
                      formatTime(
                          entry.timestamp
                      );


            note=
                data.note || "";

            break;


        case "meal":

            title=
                escapeHTML(
                    data.mealType || "Meal"
                );


            meta=
                data.eventTime

                    ? "At " +
                      formatEventTime(
                          data.eventTime
                      )

                    : "Logged " +
                      formatTime(
                          entry.timestamp
                      );


            note=
                data.food || "";

            break;


        case "movement":

            title=
                escapeHTML(
                    (data.activity || "Activity") +
                    (
                        data.minutes
                            ? " · " +
                              data.minutes +
                              " min"
                            : ""
                    )
                );


            meta=
                "Logged " +
                formatTime(
                    entry.timestamp
                );


            note=
                data.note || "";

            break;

    }


    return`

        <div class="tr-w005-history-row">

            <div class="tr-w005-history-row-main">

                <strong>
                    ${title}
                </strong>

                <span>
                    ${escapeHTML(meta)}
                </span>

            </div>

            ${
                note
                    ? `
                        <div class="tr-w005-history-note">
                            ${escapeHTML(note)}
                        </div>
                    `
                    : ""
            }

        </div>

    `;

}


/*==============================================================================*
*FORM BUILDER
*==============================================================================*/

function buildForm(type){

    const latest=
        getLatest(type);


    let data={};


    if(
        type!=="bloodSugar" &&
        type!=="meal" &&
        type!=="movement"
    ){

        data=
            latest &&
            latest.data
                ? latest.data
                : {};

    }


    activeChoices={};


    switch(type){


        case "bloodSugar":

            activeChoices.context="";


            return`

                <div class="tr-w005-field">

                    <label>
                        Glucose Reading
                    </label>

                    <input
                        type="number"
                        min="20"
                        max="600"
                        step="1"
                        id="trW005BloodSugarValue"
                        value=""
                        placeholder="e.g. 142"
                    >

                    <span class="tr-w005-help">
                        Enter the reading shown on your glucose meter.
                    </span>

                </div>


                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Reading Context
                    </span>

                    ${createOptions(

                        "context",

                        [
                            {value:"Fasting",label:"Fasting"},
                            {value:"Before Meal",label:"Before Meal"},
                            {value:"After Meal",label:"After Meal"},
                            {value:"Random",label:"Random"},
                            {value:"Bedtime",label:"Bedtime"}
                        ],

                        ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        When was this reading taken?
                    </label>

                    <input
                        type="time"
                        id="trW005BloodSugarTime"
                        value="${escapeHTML(
                            getCurrentTimeValue()
                        )}"
                    >

                    <span class="tr-w005-help">
                        Change this if the reading was taken earlier.
                    </span>

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    ></textarea>

                </div>


                ${buildHistoryHTML(
                    "bloodSugar"
                )}

            `;


        case "meal":

            activeChoices.mealType="";


            return`

                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Meal
                    </span>

                    ${createOptions(

                        "mealType",

                        [
                            {value:"Breakfast",label:"Breakfast"},
                            {value:"Lunch",label:"Lunch"},
                            {value:"Dinner",label:"Dinner"},
                            {value:"Snack",label:"Snack"}
                        ],

                        ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        When did you have it?
                    </label>

                    <input
                        type="time"
                        id="trW005MealTime"
                        value="${escapeHTML(
                            getCurrentTimeValue()
                        )}"
                    >

                    <span class="tr-w005-help">
                        Change this if you had the meal earlier.
                    </span>

                </div>


                <div class="tr-w005-field">

                    <label>
                        What did you have?
                    </label>

                    <textarea
                        id="trW005MealNote"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="e.g. dal, roti and salad"
                    ></textarea>

                    <span class="tr-w005-help">
                        A short description is enough.
                    </span>

                </div>


                ${buildHistoryHTML(
                    "meal"
                )}

            `;


        case "medication":

            activeChoices.status=
                data.status || "";


            return`

                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Medication Status
                    </span>

                    ${createOptions(

                        "status",

                        [
                            {value:"Taken",label:"Taken"},
                            {value:"Missed",label:"Missed"},
                            {value:"Not Applicable",label:"Not Applicable"}
                        ],

                        data.status || ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        When?
                    </label>

                    <input
                        type="time"
                        id="trW005MedicationTime"
                        value="${escapeHTML(
                            data.eventTime ||
                            getCurrentTimeValue()
                        )}"
                    >

                    <span class="tr-w005-help">
                        Change this if the medication was taken or missed earlier.
                    </span>

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    >${escapeHTML(
                        data.note || ""
                    )}</textarea>

                </div>

            `;


        case "water":

            return`

                <div class="tr-w005-field">

                    <label>
                        Glasses of Water Today
                    </label>

                    <input
                        type="number"
                        min="0"
                        max="30"
                        step="1"
                        id="trW005WaterGlasses"
                        value="${escapeHTML(
                            typeof data.glasses!=="undefined"
                                ? data.glasses
                                : ""
                        )}"
                        placeholder="e.g. 5"
                    >

                    <span class="tr-w005-help">
                        Enter your current total for today.
                    </span>

                </div>

            `;


        case "movement":

            activeChoices.activity="";


            return`

                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Activity
                    </span>

                    ${createOptions(

                        "activity",

                        [
                            {value:"Walking",label:"Walking"},
                            {value:"Exercise",label:"Exercise"},
                            {value:"Yoga",label:"Yoga"},
                            {value:"Other",label:"Other"}
                        ],

                        ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        Duration (minutes)
                    </label>

                    <input
                        type="number"
                        min="1"
                        max="600"
                        step="1"
                        id="trW005MovementMinutes"
                        value=""
                        placeholder="e.g. 20"
                    >

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    ></textarea>

                </div>


                ${buildHistoryHTML(
                    "movement"
                )}

            `;


        case "mood":

            activeChoices.mood=
                data.mood || "";


            return`

                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        How are you feeling?
                    </span>

                    ${createOptions(

                        "mood",

                        [
                            {value:"Good",label:"Good"},
                            {value:"Okay",label:"Okay"},
                            {value:"Low",label:"Low"}
                        ],

                        data.mood || ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    >${escapeHTML(
                        data.note || ""
                    )}</textarea>

                </div>

            `;


        case "stress":

            activeChoices.level=
                data.level || "";

            activeChoices.source=
                data.source || "";


            return`

                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Stress Level
                    </span>

                    ${createOptions(

                        "level",

                        [
                            {value:"Low",label:"Low"},
                            {value:"Moderate",label:"Moderate"},
                            {value:"High",label:"High"}
                        ],

                        data.level || ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        What is the main source?
                    </span>

                    ${createOptions(

                        "source",

                        [
                            {value:"Professional / Work",label:"Professional / Work"},
                            {value:"Family",label:"Family"},
                            {value:"Personal",label:"Personal"},
                            {value:"Social",label:"Social"},
                            {value:"Other",label:"Other"},
                            {value:"Prefer not to say",label:"Prefer not to say"}
                        ],

                        data.source || ""

                    )}

                    <span class="tr-w005-help">
                        Optional. Choose only if it feels useful to record.
                    </span>

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    >${escapeHTML(
                        data.note || ""
                    )}</textarea>

                </div>

            `;


        case "sleep":

            activeChoices.quality=
                data.quality || "";


            return`

                <div class="tr-w005-field">

                    <label>
                        Hours Slept
                    </label>

                    <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.5"
                        id="trW005SleepHours"
                        value="${escapeHTML(
                            typeof data.hours!=="undefined"
                                ? data.hours
                                : ""
                        )}"
                        placeholder="e.g. 7"
                    >

                </div>


                <div class="tr-w005-field">

                    <span class="tr-w005-field-label">
                        Sleep Quality
                    </span>

                    ${createOptions(

                        "quality",

                        [
                            {value:"Good",label:"Good"},
                            {value:"Average",label:"Average"},
                            {value:"Poor",label:"Poor"}
                        ],

                        data.quality || ""

                    )}

                </div>


                <div class="tr-w005-field">

                    <label>
                        Note
                    </label>

                    <textarea
                        id="trW005Note"
                        maxlength="${W005_CONFIG.noteMaxLength}"
                        placeholder="Optional"
                    >${escapeHTML(
                        data.note || ""
                    )}</textarea>

                </div>

            `;

    }


    return"";

}


/*==============================================================================*
*COLLECT FORM DATA
*==============================================================================*/

function collectFormData(type){

    switch(type){


        case "bloodSugar":{

            const input=
                document.getElementById(
                    "trW005BloodSugarValue"
                );


            const timeInput=
                document.getElementById(
                    "trW005BloodSugarTime"
                );


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            const value=
                input
                    ? numberValue(
                        input.value
                    )
                    : null;


            if(
                value===null ||
                value<=0
            ){

                alert(
                    "Please enter a valid blood sugar reading."
                );

                return null;

            }


            return{

                value:value,

                unit:"mg/dL",

                context:
                    activeChoices.context || "",

                eventTime:
                    timeInput
                        ? timeInput.value
                        : "",

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }


        case "meal":{

            const foodInput=
                document.getElementById(
                    "trW005MealNote"
                );


            const timeInput=
                document.getElementById(
                    "trW005MealTime"
                );


            const food=
                cleanNote(
                    foodInput
                        ? foodInput.value
                        : ""
                );


            if(
                !activeChoices.mealType &&
                !food
            ){

                alert(
                    "Please select a meal or add a short food note."
                );

                return null;

            }


            return{

                mealType:
                    activeChoices.mealType || "Meal",

                eventTime:
                    timeInput
                        ? timeInput.value
                        : "",

                food:food

            };

        }


        case "medication":{

            if(
                !activeChoices.status
            ){

                alert(
                    "Please select a medication status."
                );

                return null;

            }


            const timeInput=
                document.getElementById(
                    "trW005MedicationTime"
                );


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            return{

                status:
                    activeChoices.status,

                eventTime:
                    timeInput
                        ? timeInput.value
                        : "",

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }


        case "water":{

            const input=
                document.getElementById(
                    "trW005WaterGlasses"
                );


            const glasses=
                input
                    ? numberValue(
                        input.value
                    )
                    : null;


            if(
                glasses===null ||
                glasses<0
            ){

                alert(
                    "Please enter the number of glasses."
                );

                return null;

            }


            return{

                glasses:
                    Math.round(
                        glasses
                    )

            };

        }


        case "movement":{

            const minutesInput=
                document.getElementById(
                    "trW005MovementMinutes"
                );


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            const minutes=
                minutesInput
                    ? numberValue(
                        minutesInput.value
                    )
                    : null;


            if(
                !activeChoices.activity ||
                minutes===null ||
                minutes<=0
            ){

                alert(
                    "Please select an activity and enter its duration."
                );

                return null;

            }


            return{

                activity:
                    activeChoices.activity,

                minutes:
                    Math.round(
                        minutes
                    ),

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }


        case "mood":{

            if(
                !activeChoices.mood
            ){

                alert(
                    "Please select how you are feeling."
                );

                return null;

            }


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            return{

                mood:
                    activeChoices.mood,

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }


        case "stress":{

            if(
                !activeChoices.level
            ){

                alert(
                    "Please select your stress level."
                );

                return null;

            }


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            return{

                level:
                    activeChoices.level,

                source:
                    activeChoices.source || "",

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }


        case "sleep":{

            const hoursInput=
                document.getElementById(
                    "trW005SleepHours"
                );


            const noteInput=
                document.getElementById(
                    "trW005Note"
                );


            const hours=
                hoursInput
                    ? numberValue(
                        hoursInput.value
                    )
                    : null;


            if(
                hours===null ||
                hours<0 ||
                hours>24
            ){

                alert(
                    "Please enter valid sleep hours."
                );

                return null;

            }


            if(
                !activeChoices.quality
            ){

                alert(
                    "Please select your sleep quality."
                );

                return null;

            }


            return{

                hours:hours,

                quality:
                    activeChoices.quality,

                note:
                    cleanNote(
                        noteInput
                            ? noteInput.value
                            : ""
                    )

            };

        }

    }


    return null;

}


/*==============================================================================*
*CARD DISPLAY VALUE
*==============================================================================*/

function getDisplayValue(type,entry){

    if(
        !entry ||
        !entry.data
    ){

        return CHECKINS[type].emptyText;

    }


    const data=
        entry.data;


    switch(type){


        case "bloodSugar":

            return(
                data.value +
                " " +
                (data.unit || "mg/dL") +
                (
                    data.context
                        ? " · " + data.context
                        : ""
                )
            );


        case "meal":

            return(
                (data.mealType || "Meal") +
                (
                    data.food
                        ? " · " + data.food
                        : ""
                )
            );


        case "medication":

            return(
                data.status || "Add status"
            );


        case "water":

            return(
                data.glasses +
                (
                    Number(data.glasses)===1
                        ? " glass"
                        : " glasses"
                )
            );


        case "movement":

            return(
                (data.activity || "Activity") +
                " · " +
                data.minutes +
                " min"
            );


        case "mood":

            return(
                data.mood || "How do you feel?"
            );


        case "stress":

            return(
                (data.level || "") +
                (
                    data.level
                        ? " stress"
                        : ""
                )
            );


        case "sleep":

            return(
                data.hours +
                " hrs · " +
                (data.quality || "")
            );

    }


    return CHECKINS[type].emptyText;

}


/*==============================================================================*
*CARD TIME LABEL
*==============================================================================*/

function getCardTimeLabel(entry){

    if(!entry){

        return"";

    }


    const time=
        formatTime(
            entry.updatedAt ||
            entry.timestamp
        );


    if(!time){

        return"";

    }


    return(
        "Updated " +
        time
    );

}


/*==============================================================================*
*RENDER ALL CARDS
*==============================================================================*/

function renderCards(){

    if(!root){

        return;

    }


    Object.keys(
        CHECKINS
    ).forEach(

        function(type){


            const card=
                root.querySelector(
                    `.tr-w005-item[data-checkin="${type}"]`
                );


            const value=
                root.querySelector(
                    `[data-value-for="${type}"]`
                );


            const time=
                root.querySelector(
                    `[data-time-for="${type}"]`
                );


            if(
                !card ||
                !value ||
                !time
            ){

                return;

            }


            const entry=
                getLatest(type);


            if(!entry){

                card.classList.remove(
                    "is-logged"
                );


                value.textContent=
                    CHECKINS[type].emptyText;


                time.textContent="";


                return;

            }


            card.classList.add(
                "is-logged"
            );


            value.textContent=
                getDisplayValue(
                    type,
                    entry
                );


            time.textContent=
                getCardTimeLabel(
                    entry
                );

        }

    );


    renderDayStatus();

}


/*==============================================================================*
*DAY STATUS
*==============================================================================*/

function renderDayStatus(){

    if(!root){

        return;

    }


    const status=
        root.querySelector(
            "#trW005DayStatus"
        );


    if(!status){

        return;

    }


    const count=
        Object.keys(
            CHECKINS
        ).filter(

            function(type){

                return Boolean(
                    getLatest(type)
                );

            }

        ).length;


    if(count===0){

        status.textContent=
            "Nothing logged yet";

        return;

    }


    if(count===1){

        status.textContent=
            "1 wellness entry today";

        return;

    }


    status.textContent=
        count +
        " wellness entries today";

}


/*==============================================================================*
*SHOW SAVE / UPDATE FEEDBACK
*==============================================================================*/

function showSaveFeedback(wasUpdate){

    if(!root){

        return;

    }


    const feedback=
        root.querySelector(
            "#trW005SaveFeedback"
        );


    const title=
        root.querySelector(
            "#trW005FeedbackTitle"
        );


    const text=
        root.querySelector(
            "#trW005FeedbackText"
        );


    if(
        !feedback ||
        !title ||
        !text
    ){

        return;

    }


    if(feedbackTimer){

        clearTimeout(
            feedbackTimer
        );

        feedbackTimer=null;

    }


    if(wasUpdate){

        title.textContent=
            "✓ Wellness log updated";


        text.textContent=
            "Your latest entry will be reflected in your TRESTLE Insight.";

    }

    else{

        title.textContent=
            "✓ Wellness log saved";


        text.textContent=
            "It will be reflected in your TRESTLE Insight.";

    }


    feedback.hidden=false;


    feedback.scrollIntoView({

        behavior:"smooth",

        block:"nearest"

    });


    feedbackTimer=
        setTimeout(

            function(){

                feedback.hidden=true;

            },

            W005_CONFIG.feedbackDuration

        );

}


/*==============================================================================*
*OPEN MODAL
*==============================================================================*/

function openModal(type){

    if(
        !root ||
        !CHECKINS[type]
    ){

        return;

    }


    activeType=
        type;


    const modal=
        root.querySelector(
            "#trW005Modal"
        );


    const title=
        root.querySelector(
            "#trW005ModalTitle"
        );


    const icon=
        root.querySelector(
            "#trW005ModalIcon"
        );


    const body=
        root.querySelector(
            "#trW005ModalBody"
        );


    if(
        !modal ||
        !title ||
        !icon ||
        !body
    ){

        return;

    }


    title.textContent=
        CHECKINS[type].title;


    icon.textContent=
        CHECKINS[type].icon;


    body.innerHTML=
        buildForm(type);


    modal.hidden=false;


    document.body.style.overflow=
        "hidden";

}


/*==============================================================================*
*CLOSE MODAL
*==============================================================================*/

function closeModal(){

    if(!root){

        return;

    }


    const modal=
        root.querySelector(
            "#trW005Modal"
        );


    const body=
        root.querySelector(
            "#trW005ModalBody"
        );


    if(modal){

        modal.hidden=true;

    }


    if(body){

        body.innerHTML="";

    }


    activeType=null;

    activeChoices={};


    document.body.style.overflow="";

}


/*==============================================================================*
*CHOICE BUTTON
*==============================================================================*/

function handleOptionClick(button){

    const name=
        button.dataset.choiceName;


    const value=
        button.dataset.choiceValue;


    if(
        !name ||
        typeof value==="undefined"
    ){

        return;

    }


    activeChoices[name]=
        value;


    const group=
        button.closest(
            "[data-choice-group]"
        );


    if(!group){

        return;

    }


    group
        .querySelectorAll(
            ".tr-w005-option"
        )
        .forEach(

            function(item){

                item.classList.remove(
                    "is-active"
                );

            }

        );


    button.classList.add(
        "is-active"
    );

}

/*==============================================================================*
*ONE THING NOW
*
*Immediate contextual response after a Wellness Log save.
*
*IMPORTANT
*---------
*• Does not change W005 wellness data.
*• Does not feed TRESTLE Insight.
*• Does not change W007.
*• Does not give medication dosing advice.
*• Does not interpret blood glucose clinically.
*• Uses W002 profile only as contextual weighting.
*• Stores only minimal repetition-control information.
*==============================================================================*/

const ONE_THING_CONFIG={

    storageKey:
        "trestle_one_thing",

    profileStorageKey:
        "trestleProfile",

    showDelay:
        300,

    actionCooldownMinutes:
        120,

    familyCooldownMinutes:
        60,

    minimumScore:
        3

};


const ONE_THING_IMAGES={

    HYDRATE:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Water.webp?v=1786246420",

    MOVE:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Movement.webp?v=1786246420",

    NEXT_MEAL:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Meal.webp?v=1786246420",

    PAUSE_RESET:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Mood.webp?v=1786246420",

    WIND_DOWN:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Sleep.webp?v=1786246421",

    BLOOD_SUGAR:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Blood_Sugar.webp?v=1786246419",

    MEDICATION:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Medication.webp?v=1786246420",

    GENERAL:
        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Mood.webp?v=1786246420"

};


const ONE_THING_ACTIONS={

    HYDRATE_GLASS:{
        family:"HYDRATE",
        headline:"Have a glass of water"
    },

    HYDRATE_BREAK:{
        family:"HYDRATE",
        headline:"Make your next break a water break"
    },

    MOVE_POST_MEAL:{
        family:"MOVE",
        headline:"Take a short walk after your meal"
    },

    MOVE_DESK_BREAK:{
        family:"MOVE",
        headline:"Take a short movement break"
    },

    MOVE_STRETCH:{
        family:"MOVE",
        headline:"Stand up and stretch for a few minutes"
    },

        MEAL_BALANCE:{
        family:"NEXT_MEAL",
        headline:"Keep your next meal balanced"
    },

    RESET_TWO_MIN:{
        family:"PAUSE_RESET",
        headline:"Give yourself two quiet minutes"
    },

    RESET_SCREEN:{
        family:"PAUSE_RESET",
        headline:"Take a short screen break"
    },

    WIND_DOWN_EARLY:{
        family:"WIND_DOWN",
        headline:"Give yourself a quieter evening"
    },

    WIND_DOWN_ROUTINE:{
        family:"WIND_DOWN",
        headline:"Start winding down a little earlier tonight"
    }

};


/*==============================================================================*
*READ W002 PROFILE
*==============================================================================*/

function getOneThingProfile(){

    try{

        const raw=
            localStorage.getItem(
                ONE_THING_CONFIG.profileStorageKey
            );


        if(!raw){

            return{};

        }


        const profile=
            JSON.parse(raw);


        if(
            !profile ||
            typeof profile!=="object"
        ){

            return{};

        }


        return profile;

    }

    catch(error){

        console.warn(
            "TRESTLE One Thing profile read error",
            error
        );


        return{};

    }

}


/*==============================================================================*
*MINIMAL REPETITION STATE
*==============================================================================*/

function readOneThingState(){

    try{

        const raw=
            localStorage.getItem(
                ONE_THING_CONFIG.storageKey
            );


        if(!raw){

            return{
                recent:[]
            };

        }


        const parsed=
            JSON.parse(raw);


        if(
            !parsed ||
            typeof parsed!=="object"
        ){

            return{
                recent:[]
            };

        }


        if(
            !Array.isArray(parsed.recent)
        ){

            parsed.recent=[];

        }


        return parsed;

    }

    catch(error){

        return{
            recent:[]
        };

    }

}


function rememberOneThing(actionId,family){

    if(!actionId){

        return;

    }


    try{

        const state=
            readOneThingState();


        const now=
            getTimestamp();


        state.recent.unshift({

            actionId:
                actionId,

            family:
                family || "",

            shownAt:
                now

        });


        state.recent=
            state.recent.slice(
                0,
                12
            );


        localStorage.setItem(
            ONE_THING_CONFIG.storageKey,
            JSON.stringify(state)
        );

    }

    catch(error){

        console.warn(
            "TRESTLE One Thing repetition state could not be saved",
            error
        );

    }

}


function minutesSince(timestamp){

    if(!timestamp){

        return Infinity;

    }


    const date=
        new Date(timestamp);


    if(
        Number.isNaN(
            date.getTime()
        )
    ){

        return Infinity;

    }


    return(
        Date.now() -
        date.getTime()
    ) / 60000;

}


function getRepetitionPenalty(actionId,family){

    const state=
        readOneThingState();


    let penalty=0;


    state.recent.forEach(

        function(item){

            const age=
                minutesSince(
                    item.shownAt
                );


            if(
                item.actionId===actionId &&
                age<
                ONE_THING_CONFIG.actionCooldownMinutes
            ){

                penalty=
                    Math.max(
                        penalty,
                        8
                    );

            }


            else if(
                item.family===family &&
                age<
                ONE_THING_CONFIG.familyCooldownMinutes
            ){

                penalty=
                    Math.max(
                        penalty,
                        4
                    );

            }

        }

    );


    return penalty;

}


/*==============================================================================*
*PROMISE WEIGHT
*==============================================================================*/

function getPromiseWeight(promise,family){

    const weights={

        "More Calmness":{
            PAUSE_RESET:3,
            WIND_DOWN:2,
            MOVE:1
        },

        "More Energy":{
            MOVE:3,
            HYDRATE:2,
            NEXT_MEAL:1
        },

        "Better Focus":{
            PAUSE_RESET:3,
            HYDRATE:2,
            MOVE:1
        },

        "Healthier Living":{
            NEXT_MEAL:3,
            MOVE:3,
            HYDRATE:1,
            WIND_DOWN:1
        },

        "Better Sleep":{
            WIND_DOWN:3,
            PAUSE_RESET:2,
            MOVE:1
        },

        "Balanced Life":{
            HYDRATE:1,
            MOVE:1,
            NEXT_MEAL:1,
            PAUSE_RESET:1,
            WIND_DOWN:1
        }

    };


    if(
        !promise ||
        !weights[promise]
    ){

        return 0;

    }


    return(
        weights[promise][family] ||
        0
    );

}


/*==============================================================================*
*PROMISE SUPPORTING COPY
*==============================================================================*/

function getPromiseMessage(promise,family){

    if(!promise){

        return"One small action may fit well with the way your day is developing.";

    }


    const messages={

        HYDRATE:
            "A little attention to hydration fits well with your promise of " +
            promise +
            " today.",

        MOVE:
            "A little movement fits well with your promise of " +
            promise +
            " today.",

        NEXT_MEAL:
            "A small food choice can support your promise of " +
            promise +
            " today.",

        PAUSE_RESET:
            "A brief pause may be useful right now — and fits with your promise of " +
            promise +
            ".",

        WIND_DOWN:
            "A gentler evening can support your promise of " +
            promise +
            " today."

    };


    return(
        messages[family] ||
        "One small action may fit well with your promise of " +
        promise +
        " today."
    );

}


/*==============================================================================*
*TODAY'S MOVEMENT TOTAL
*==============================================================================*/

function getMovementMinutesToday(){

    const history=
        getHistory(
            "movement"
        );


    return history.reduce(

        function(total,entry){

            if(
                !entry ||
                !entry.data
            ){

                return total;

            }


            const minutes=
                Number(
                    entry.data.minutes
                );


            return total +
                (
                    Number.isFinite(minutes)
                        ? minutes
                        : 0
                );

        },

        0

    );

}


/*==============================================================================*
*MEAL RECENCY
*==============================================================================*/

function getMinutesSinceMeal(){

    const meal=
        getLatest(
            "meal"
        );


    if(
        !meal ||
        !meal.data
    ){

        return Infinity;

    }


    /*
    Event time is preferred because a meal may be logged later.
    */

    if(meal.data.eventTime){

        const parts=
            String(
                meal.data.eventTime
            ).split(":");


        if(parts.length>=2){

            const now=
                new Date();


            const mealDate=
                new Date();


            mealDate.setHours(
                Number(parts[0]),
                Number(parts[1]),
                0,
                0
            );


            /*
            Avoid treating a future clock time as a recent meal.
            */

            if(
                mealDate.getTime()<=
                now.getTime()
            ){

                return(
                    now.getTime() -
                    mealDate.getTime()
                ) / 60000;

            }

        }

    }


    return minutesSince(
        meal.timestamp
    );

}



/*==============================================================================*
*LIFE STAGE MODIFIER
*==============================================================================*/

function getLifeStageModifier(profile,family){

    const stage=
        profile.lifeStage || "";


    if(
        stage==="Perimenopause" ||
        stage==="Menopause"
    ){

        if(
            family==="WIND_DOWN" ||
            family==="PAUSE_RESET"
        ){

            return 1;

        }

    }


    /*
    Pregnancy-related stages deliberately do not create
    stronger health recommendations in v1.
    */

    return 0;

}


/*==============================================================================*
*DIABETES SAFETY MODIFIER
*==============================================================================*/

function getDiabetesModifier(profile,family){

    const diabetes=
        profile.diabetesType || "";


    /*
    "Not Sure" is retained internally for compatibility and now
    represents the visible "No known diabetes" option.
    */

    if(
        !diabetes ||
        diabetes==="Not Sure"
    ){

        return 0;

    }


    /*
    Type 1 and Gestational Diabetes receive no additional
    food/movement weighting in this lightweight engine.
    */

    if(
        diabetes==="Type 1 Diabetes" ||
        diabetes==="Gestational Diabetes"
    ){

        return 0;

    }


    if(
        (
            diabetes==="Prediabetes" ||
            diabetes==="Type 2 Diabetes" ||
            diabetes==="Previous Gestational Diabetes"
        ) &&
        (
            family==="MOVE" ||
            family==="NEXT_MEAL"
        )
    ){

        return 1;

    }


    return 0;

}


/*==============================================================================*
*BUILD CANDIDATE
*==============================================================================*/

function createOneThingCandidate(
    actionId,
    contextScore,
    profile
){

    const action=
        ONE_THING_ACTIONS[actionId];


    if(!action){

        return null;

    }


    const promise=
        profile.promise || "";


    const score=

        contextScore +

        getPromiseWeight(
            promise,
            action.family
        ) +

        getLifeStageModifier(
            profile,
            action.family
        ) +

        getDiabetesModifier(
            profile,
            action.family
        ) -

        getRepetitionPenalty(
            actionId,
            action.family
        );


    return{

        actionId:
            actionId,

        family:
            action.family,

        headline:
            action.headline,

        score:
            score,

        message:
            getPromiseMessage(
                promise,
                action.family
            ),

        image:
            ONE_THING_IMAGES[action.family] ||
            ONE_THING_IMAGES.GENERAL

    };

}


/*==============================================================================*
*SELECT ONE THING
*==============================================================================*/

function chooseOneThing(){

    const profile=
        getOneThingProfile();


    const candidates=[];


    const water=
        getLatest(
            "water"
        );


    const stress=
        getLatest(
            "stress"
        );


    const mood=
        getLatest(
            "mood"
        );


    const sleep=
        getLatest(
            "sleep"
        );


    const meal=
        getLatest(
            "meal"
        );


    const movementMinutes=
        getMovementMinutesToday();


    const hour=
        new Date().getHours();


    const mealAge=
        getMinutesSinceMeal();


    /*--------------------------------------------------------------------------
    HYDRATION
    --------------------------------------------------------------------------*/

    if(
        water &&
        water.data
    ){

        const glasses=
            Number(
                water.data.glasses
            );


        /*
        Deliberately avoids defining a universal daily water target.
        We only identify an obvious low-log opportunity.
        */

        if(
            Number.isFinite(glasses) &&
            glasses<=2 &&
            hour>=10
        ){

            candidates.push(
                createOneThingCandidate(
                    "HYDRATE_GLASS",
                    3,
                    profile
                )
            );

        }


        else if(
            Number.isFinite(glasses) &&
            glasses<=4 &&
            hour>=14
        ){

            candidates.push(
                createOneThingCandidate(
                    "HYDRATE_BREAK",
                    2,
                    profile
                )
            );

        }

    }


    /*--------------------------------------------------------------------------
    POST-MEAL MOVEMENT

    No glucose reading is used to trigger this.
    --------------------------------------------------------------------------*/

    if(
        meal &&
        mealAge>=0 &&
        mealAge<=120 &&
        movementMinutes<20
    ){

        candidates.push(
            createOneThingCandidate(
                "MOVE_POST_MEAL",
                4,
                profile
            )
        );

    }


    /*--------------------------------------------------------------------------
    GENERAL MOVEMENT
    --------------------------------------------------------------------------*/

    if(
        movementMinutes===0 &&
        hour>=11
    ){

        candidates.push(
            createOneThingCandidate(
                "MOVE_DESK_BREAK",
                2,
                profile
            )
        );

    }


    if(
        movementMinutes>0 &&
        movementMinutes<15 &&
        hour>=12
    ){

        candidates.push(
            createOneThingCandidate(
                "MOVE_STRETCH",
                2,
                profile
            )
        );

    }


        /*--------------------------------------------------------------------------
    NEXT MEAL

    V1 deliberately does not infer missing protein, fibre or
    other nutrients from the user's short meal description.
    --------------------------------------------------------------------------*/

    if(meal){

        candidates.push(
            createOneThingCandidate(
                "MEAL_BALANCE",
                1,
                profile
            )
        );

    }


    /*--------------------------------------------------------------------------
    STRESS + MOOD
    --------------------------------------------------------------------------*/

    if(
        stress &&
        stress.data &&
        (
            stress.data.level==="Moderate" ||
            stress.data.level==="High"
        )
    ){

        let resetScore=3;


        if(
            mood &&
            mood.data &&
            (
                mood.data.mood==="Okay" ||
                mood.data.mood==="Low"
            )
        ){

            resetScore+=2;

        }


        candidates.push(
            createOneThingCandidate(
                "RESET_TWO_MIN",
                resetScore,
                profile
            )
        );


        if(
            stress.data.source==="Professional / Work"
        ){

            candidates.push(
                createOneThingCandidate(
                    "RESET_SCREEN",
                    resetScore,
                    profile
                )
            );

        }

    }


    /*--------------------------------------------------------------------------
    WIND DOWN

    Only later in the day.
    --------------------------------------------------------------------------*/

    if(
        sleep &&
        sleep.data &&
        hour>=17
    ){

        if(
            sleep.data.quality==="Poor"
        ){

            candidates.push(
                createOneThingCandidate(
                    "WIND_DOWN_ROUTINE",
                    4,
                    profile
                )
            );

        }


        else if(
            Number(sleep.data.hours)<6.5
        ){

            candidates.push(
                createOneThingCandidate(
                    "WIND_DOWN_EARLY",
                    3,
                    profile
                )
            );

        }

    }


    const validCandidates=
        candidates.filter(Boolean);


    if(!validCandidates.length){

        return null;

    }


    validCandidates.sort(

        function(a,b){

            return b.score-a.score;

        }

    );


    const winner=
        validCandidates[0];


    if(
        winner.score<
        ONE_THING_CONFIG.minimumScore
    ){

        return null;

    }


    return winner;

}


/*==============================================================================*
*SPECIAL NON-ACTION RESPONSES
*==============================================================================*/

function getSpecialOneThingResponse(savedType,savedEntry){

    if(
        savedType==="bloodSugar"
    ){

        return{

            kind:"acknowledgement",

            eyebrow:"LOGGED",

            headline:"Blood sugar reading recorded",

            message:
                "TRESTLE will keep this reading as part of today's Wellness Log. No extra action is being suggested from this reading alone.",

            image:
                ONE_THING_IMAGES.BLOOD_SUGAR

        };

    }


    if(
        savedType==="medication" &&
        savedEntry &&
        savedEntry.data
    ){

        if(
            savedEntry.data.status==="Missed"
        ){

            return{

                kind:"safety",

                eyebrow:"MEDICATION UPDATED",

                headline:"You've recorded this dose as missed",

                message:
                    "Follow the instructions you've been given for missed medication, or contact your healthcare professional or pharmacist if you're unsure.",

                image:
                    ONE_THING_IMAGES.MEDICATION

            };

        }


        if(
            savedEntry.data.status==="Taken"
        ){

            return{

                kind:"acknowledgement",

                eyebrow:"MEDICATION UPDATED",

                headline:"Medication logged",

                message:
                    "Nothing else is needed from TRESTLE right now.",

                image:
                    ONE_THING_IMAGES.MEDICATION

            };

        }


        return{

            kind:"acknowledgement",

            eyebrow:"LOGGED",

            headline:"Medication status recorded",

            message:
                "TRESTLE will keep this in mind as your day develops.",

            image:
                ONE_THING_IMAGES.MEDICATION

        };

    }


    /*
    If meaningful movement has just been logged,
    do not immediately ask the user to move again.
    */

    if(
        savedType==="movement" &&
        savedEntry &&
        savedEntry.data &&
        Number(savedEntry.data.minutes)>=15
    ){

        return{

            kind:"positive",

            eyebrow:"YOU'RE ON TRACK",

            headline:"Movement logged",

            message:
                "Nothing extra right now. Keep your rhythm.",

            image:
                ONE_THING_IMAGES.MOVE

        };

    }


    return null;

}


/*==============================================================================*
*DEFAULT ACKNOWLEDGEMENT
*==============================================================================*/

function getDefaultOneThingResponse(){

    return{

        kind:"acknowledgement",

        eyebrow:"LOGGED",

        headline:"That's in your Wellness Log",

        message:
            "TRESTLE will keep this in mind as your day develops.",

        image:
            ONE_THING_IMAGES.GENERAL

    };

}


/*==============================================================================*
*OPEN ONE THING MODAL
*==============================================================================*/

function openOneThingModal(response){

    if(
        !root ||
        !response
    ){

        return;

    }


    const modal=
        root.querySelector(
            "#trW005OneThingModal"
        );


    const eyebrow=
        root.querySelector(
            "#trW005OneThingEyebrow"
        );


    const title=
        root.querySelector(
            "#trW005OneThingTitle"
        );


    const message=
        root.querySelector(
            "#trW005OneThingMessage"
        );


    const image=
        root.querySelector(
            "#trW005OneThingImage"
        );


    const dialog=
        root.querySelector(
            ".tr-w005-one-dialog"
        );


    if(
        !modal ||
        !eyebrow ||
        !title ||
        !message ||
        !image
    ){

        return;

    }


    eyebrow.textContent=
        response.eyebrow ||
        "ONE THING NOW";


    title.textContent=
        response.headline ||
        "One small thing for right now";


    message.textContent=
        response.message || "";


    image.src=
        response.image ||
        ONE_THING_IMAGES.GENERAL;


    image.alt="";


    if(dialog){

        dialog.dataset.responseKind=
            response.kind ||
            "action";

    }


    modal.hidden=false;


    document.body.style.overflow=
        "hidden";

}


/*==============================================================================*
*CLOSE ONE THING MODAL
*==============================================================================*/

function closeOneThingModal(){

    if(!root){

        return;

    }


    const modal=
        root.querySelector(
            "#trW005OneThingModal"
        );


    if(modal){

        modal.hidden=true;

    }


    document.body.style.overflow="";

}


/*==============================================================================*
*SCHEDULE ONE THING
*==============================================================================*/

function scheduleOneThing(savedType,savedEntry){

    let response=
        getSpecialOneThingResponse(
            savedType,
            savedEntry
        );


    if(!response){

        const action=
            chooseOneThing();


        if(action){

            response={

                kind:"action",

                eyebrow:"ONE THING NOW",

                headline:
                    action.headline,

                message:
                    action.message,

                image:
                    action.image

            };


            rememberOneThing(
                action.actionId,
                action.family
            );

        }

    }


    if(!response){

        response=
            getDefaultOneThingResponse();

    }


    setTimeout(

        function(){

            openOneThingModal(
                response
            );

        },

        ONE_THING_CONFIG.showDelay

    );

}
/*==============================================================================*
*SAVE ACTIVE WELLNESS ENTRY
*==============================================================================*/

function saveActiveCheckin(){

    if(!activeType){

        return;

    }


    const savedType=
        activeType;


    /*
    Determine whether this category already had a latest value
    BEFORE saving.

    For Blood Sugar, Meal and Movement every save remains a new
    historical event, even though latest[type] is also updated.

    For the other categories this distinction tells the user whether
    she saved a new wellness entry or updated today's existing state.
    */

    const previousEntry=
        getLatest(
            savedType
        );


    const payload=
        collectFormData(
            savedType
        );


    if(!payload){

        return;

    }


    const savedEntry=
        saveCheckin(
            savedType,
            payload
        );


    if(!savedEntry){

        alert(
            "Your wellness log entry could not be saved."
        );

        return;

    }


    const wasUpdate=

        CHECKINS[savedType].history===false &&
        Boolean(previousEntry);


    renderCards();


       closeModal();


    showSaveFeedback(
        wasUpdate
    );


    /*
    Immediate contextual response.
    This is independent of TRESTLE Insight / W007.
    */

    scheduleOneThing(
        savedType,
        savedEntry
    );


    /*==========================================================================
    BROADCAST UPDATE

    Existing event names deliberately retained for backward compatibility.
    ==========================================================================*/

    window.dispatchEvent(

        new CustomEvent(

            "trestle-w005-updated",

            {

                detail:{

                    date:
                        getTodayKey(),

                    type:
                        savedType,

                    entry:
                        JSON.parse(
                            JSON.stringify(
                                savedEntry
                            )
                        )

                }

            }

        )

    );


    document.dispatchEvent(

        new CustomEvent(

            "trestle:quick-checkin-updated",

            {

                detail:{

                    date:
                        getTodayKey(),

                    type:
                        savedType

                }

            }

        )

    );

}


/*==============================================================================*
*BIND EVENTS
*==============================================================================*/

function bindEvents(){

    if(
        !root ||
        eventsBound
    ){

        return;

    }


    eventsBound=true;


    root.addEventListener(

        "click",

        function(event){


            const option=
                event.target.closest(
                    ".tr-w005-option"
                );


            if(option){

                handleOptionClick(
                    option
                );

                return;

            }


            const card=
                event.target.closest(
                    ".tr-w005-item"
                );


            if(card){

                openModal(
                    card.dataset.checkin
                );

                return;

            }


            if(
                event.target.hasAttribute(
                    "data-w005-close"
                )
            ){

                closeModal();

            }

        }

    );


    const closeButton=
        root.querySelector(
            "#trW005ModalClose"
        );


    if(closeButton){

        closeButton.addEventListener(
            "click",
            closeModal
        );

    }


    const cancelButton=
        root.querySelector(
            "#trW005Cancel"
        );


    if(cancelButton){

        cancelButton.addEventListener(
            "click",
            closeModal
        );

    }


    const saveButton=
        root.querySelector(
            "#trW005Save"
        );


    if(saveButton){

        saveButton.addEventListener(
            "click",
            saveActiveCheckin
        );

    }
        const oneThingClose=
        root.querySelector(
            "#trW005OneThingClose"
        );


    if(oneThingClose){

        oneThingClose.addEventListener(
            "click",
            closeOneThingModal
        );

    }


    const oneThingGotIt=
        root.querySelector(
            "#trW005OneThingGotIt"
        );


    if(oneThingGotIt){

        oneThingGotIt.addEventListener(
            "click",
            closeOneThingModal
        );

    }


    root
        .querySelectorAll(
            "[data-one-thing-close]"
        )
        .forEach(

            function(item){

                item.addEventListener(
                    "click",
                    closeOneThingModal
                );

            }

        );


    document.addEventListener(

        "keydown",

        function(event){

            if(
                event.key==="Escape"
            ){

                const modal=
                    root.querySelector(
                        "#trW005Modal"
                    );


                                if(
                    modal &&
                    !modal.hidden
                ){

                    closeModal();

                    return;

                }


                const oneThingModal=
                    root.querySelector(
                        "#trW005OneThingModal"
                    );


                if(
                    oneThingModal &&
                    !oneThingModal.hidden
                ){

                    closeOneThingModal();

                }

            }

        }

    );

}


/*==============================================================================*
*PUBLIC API
*
*Existing interface retained.
*==============================================================================*/

window.TrestleW005={


    getToday:function(){

        if(!dailyData){

            loadToday();

        }


        return JSON.parse(
            JSON.stringify(
                dailyData
            )
        );

    },


    getLatest:function(type){

        if(!dailyData){

            loadToday();

        }


        const entry=
            getLatest(type);


        if(!entry){

            return null;

        }


        return JSON.parse(
            JSON.stringify(
                entry
            )
        );

    },


    getHistory:function(type){

        if(!dailyData){

            loadToday();

        }


        return JSON.parse(
            JSON.stringify(
                getHistory(type)
            )
        );

    },


    getDateKey:function(){

        return getTodayKey();

    },


    refresh:function(){

        loadToday();

        renderCards();


        return this.getToday();

    }

};


/*==============================================================================*
*INITIALIZE
*==============================================================================*/

function init(){

    root=
        document.getElementById(
            "trW005QuickCheckin"
        );


    if(!root){

        return;

    }


    loadToday();


    renderCards();


    bindEvents();


    console.log(
        "TRESTLE W005 Daily Wellness Log Ready"
    );

}


/*==============================================================================*
*BOOTSTRAP
*==============================================================================*/

if(
    document.readyState==="loading"
){

    document.addEventListener(
        "DOMContentLoaded",
        init
    );

}

else{

    init();

}


/*==============================================================================*
*CLOSE W005 SCOPE
*==============================================================================*/

})();